import { useCallback, useRef } from 'react';
import { useAuth } from '@/providers/AuthProvider';

interface CustomVerticalLinesHookProps {
  isCustomLineMode?: string | null;
  customVerticalLines?: any;
  onEnterCustomLineMode?: (groupId: string) => void;
  onAddAnimation?: (animationType: 'unicorn' | 'teddy', mouseX: number, mouseY: number) => void;
  onAddVerticalLine?: (lineValue: any, values: any, clickedXValue: any, label?: string, color?: string, isHarmonic?: boolean, isFirstHarmonic?: boolean, chartId?: string, sectionKey?: string, source?: 'user' | 'system') => void;
  chartId?: string;
  sectionKey?: string;
  calculateValuesAtPoint?: (event: any) => Record<string, number>; // Function to calculate sensor values at clicked point
  verticalLines?: any[]; // Pass current vertical lines to count existing custom lines
  showCreatorLabel?: boolean; // If true, adds creator username to custom line labels; if false, no creator label
}

// Global counter to track added lines per group (persists across hook instances)
const globalLineCounters: Record<string, number> = {};

export const useCustomVerticalLines = ({
  isCustomLineMode,
  customVerticalLines,
  onEnterCustomLineMode,
  onAddAnimation,
  onAddVerticalLine,
  chartId,
  sectionKey,
  verticalLines,
  showCreatorLabel = false,
}: CustomVerticalLinesHookProps) => {
  const { user } = useAuth();

  // Track if we've initialized the counter for each group
  const initializedGroups = useRef<Set<string>>(new Set());
  
  const handleCustomLineClick = useCallback((event: any) => {
    console.log('🦄 CUSTOM HOOK: handleCustomLineClick called', {
      isCustomLineMode,
      hasCustomVerticalLines: !!customVerticalLines,
      eventPoints: event?.points?.length,
      eventType: typeof event,
      hasOnAddVerticalLine: !!onAddVerticalLine
    });
    
    // Only handle if we're in custom line mode
    if (!isCustomLineMode || !customVerticalLines) {
      console.log('🦄 CUSTOM HOOK: Not handling - not in custom line mode');
      return false; // Not handled
    }

    try {
      if (!event || !event.points || event.points.length === 0) {
        return false;
      }

      const clickedXValue = event.points[0].x;
      const activeGroup = customVerticalLines.groups?.find((g: any) => g.groupId === isCustomLineMode);
      
      if (!activeGroup) {
        console.log('🦄 CUSTOM LINE MODE: No active group found');
        return false;
      }

      // Handle both numeric (Hz) and date values
      let lineXPosition: number;
      let displayValue: string;

      if (typeof clickedXValue === 'number') {
        // Numeric x-axis (frequency charts)
        lineXPosition = clickedXValue;
        displayValue = `${clickedXValue.toFixed(2)} Hz`;
      } else if (typeof clickedXValue === 'string') {
        // Date string x-axis (time-series charts)
        const dateValue = new Date(clickedXValue);
        if (isNaN(dateValue.getTime())) {
          return false;
        }
        lineXPosition = dateValue.getTime();
        displayValue = dateValue.toLocaleString();
      } else {
        return false;
      }

      // Create label based on group and existing line count from ALL sources
      const groupId = activeGroup.groupId;
      const groupPrefix = groupId === 'unicorn-events' ? 'U' :
                         groupId === 'teddy-events' ? 'T' : 'C';

      // Initialize counter from existing data if not already done
      if (!initializedGroups.current.has(groupId)) {
        // Count from customVerticalLines (API data)
        const apiCount = activeGroup.groupItems?.filter((item: any) =>
          item.source === 'user' || !item.source
        ).length || 0;

        // Count from verticalLines (already added lines in this session)
        const sessionCount = verticalLines?.filter((line: any) =>
          line.groupId === groupId || line._systemGroupId === groupId
        ).length || 0;

        // Use the higher of the two counts
        globalLineCounters[groupId] = Math.max(apiCount, sessionCount);
        initializedGroups.current.add(groupId);
      }

      // Get current count and increment
      const currentCount = globalLineCounters[groupId] || 0;
      globalLineCounters[groupId] = currentCount + 1;

      const lineLabel = `${groupPrefix}${currentCount}`;
      
      // Use the group's color or default colors based on group type  
      const groupColor = activeGroup.groupId === 'unicorn-events' ? '#9333ea' : // purple for unicorn
                        activeGroup.groupId === 'teddy-events' ? '#ec4899' : // pink for teddy
                        '#6366f1'; // default blue

      // Get username from authenticated user
      const username = user?.displayName || user?.email?.split('@')[0] || 'User';
      
      // Create new custom line item for persistence (backend data)
      const newLineItem = {
        id: `custom-${Date.now()}`,
        x: lineXPosition,
        value: showCreatorLabel ? `👤 ${username}` : lineLabel, // Add user icon and username if enabled, otherwise use label
        label: lineLabel, // Use proper label with prefix
        source: 'user',
        groupId: activeGroup.groupId,
        color: groupColor,
        timestamp: Date.now(),
        // Additional data for backend persistence
        chartContext: {
          chartId,
          sectionKey,
          clickPosition: { x: lineXPosition, y: clickedXValue }
        }
      };

      console.log('🦄 CUSTOM LINE MODE: Adding new custom line (for persistence):', newLineItem);

      // TODO: Save to backend/local storage for persistence
      // This would typically be an API call to save the new line to the group data
      // Example: await saveCustomLineToGroup(activeGroup.groupId, newLineItem);
      
      // Play animations
      if (onAddAnimation && event?.event) {
        const mouseX = event.event.pageX || 400; // Default position if not available
        const mouseY = event.event.pageY || 300;
        
        if (activeGroup.groupId === 'unicorn-events') {
          // Trigger unicorn animations
          onAddAnimation('unicorn', mouseX, mouseY);
        } else if (activeGroup.groupId === 'teddy-events') {
          // Trigger teddy bear animations
          onAddAnimation('teddy', mouseX, mouseY);
        }
      }

      // Add the vertical line to the actual chart data (visual marker only, no comparison data)
      console.log('🦄 ABOUT TO ADD LINE: Checking onAddVerticalLine callback', {
        hasCallback: !!onAddVerticalLine,
        callbackType: typeof onAddVerticalLine,
        lineXPosition,
        clickedXValue
      });
      
      // Add the line - it needs to be handled properly to show with group styling
      if (onAddVerticalLine) {
        console.log('🦄 CALLING onAddVerticalLine with custom group info:', {
          timestamp: lineXPosition,
          label: lineLabel,
          color: groupColor,
          groupId: activeGroup.groupId,
          username: username,
          showCreatorLabel,
        });

        // Add the line - store groupId and optionally username in the label
        const customLabel = showCreatorLabel
          ? `${lineLabel}|${activeGroup.groupId}|👤 ${username}`
          : `${lineLabel}|${activeGroup.groupId}`;
        onAddVerticalLine(
          lineXPosition, // timestamp
          {}, // Empty values object - will be populated by the chart component
          clickedXValue, // xValue
          customLabel, // label with groupId and optionally username encoded
          '#55687d' // Use gray color for the line
        );
      }
      
      // Also need to add to customVerticalLines somehow
      // This requires modifying the parent component to handle custom line additions
      if (customVerticalLines && activeGroup) {
        const newItem = {
          id: `custom-${Date.now()}`,
          x: lineXPosition,
          value: displayValue,
          label: lineLabel,
          source: 'user',
          color: '#55687d',
          groupId: activeGroup.groupId,
        };
        
        // Find the group and add the item
        const groupToUpdate = customVerticalLines.groups.find((g: any) => g.groupId === activeGroup.groupId);
        if (groupToUpdate && groupToUpdate.groupItems) {
          // This won't work directly since customVerticalLines is not stateful
          // But shows what needs to happen
          console.log('🦄 Need to add to group:', groupToUpdate.groupId, newItem);
        }
      }
      
      console.log('🦄 CUSTOM HOOK: Custom line click handled successfully, returning true');
      return true; // Handled
    } catch (error) {
      console.error('🦄 CUSTOM LINE MODE: Error adding custom line:', error);
      return false;
    }
  }, [isCustomLineMode, customVerticalLines, onAddAnimation, onAddVerticalLine, chartId, sectionKey, user]);

  return {
    handleCustomLineClick,
    isInCustomLineMode: !!isCustomLineMode,
  };
};